import { config } from '@/lib/config';
import type { Context } from '@/lib/store/ui';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type AppId = 'aiagent' | 'djifh2';
export type AppsTab = 'config' | 'monitor';
// `flights` is excluded alongside ds/apps: the reference mockup's
// AI_AGENT_CTXS is ['drones','oi','surv','infra','rtm','inv'] — the Flights
// context has no AI agent. Without this the widened Context union would
// silently add a seventh toggle to the Apps UI and the Apps nav badge.
export type AppsContext = Exclude<Context, 'ds' | 'apps' | 'flights'>;
export type DjiTriggerType = 'workflow' | 'mission';
export type ActivityKind = 'seed' | 'config' | 'runtime';

export interface AppActivity {
  id: string;
  appId: AppId;
  at: string;
  text: string;
  kind: ActivityKind;
}

export interface MissionTemplate {
  id: 'gps_box' | 'perimeter_sweep' | 'linear_corridor';
  name: string;
  description: string;
  hasBoxDistance: boolean;
}

export interface DjiConfig {
  orgId: string;
  apiKey: string;
  apiKeyPresent: boolean;
  brokerTopic: string;
  triggerType: DjiTriggerType;
  workflowName: string;
  missionTemplateId: MissionTemplate['id'] | null;
  boxDistanceM: number;
  configured: boolean;
  savedAt: string | null;
}

interface AppsStore {
  selectedApp: AppId | null;
  activeTab: AppsTab;
  aiEnabled: Record<AppsContext, boolean>;
  dji: DjiConfig;
  activity: Record<AppId, AppActivity[]>;

  selectApp: (app: AppId) => void;
  setActiveTab: (tab: AppsTab) => void;
  toggleAiContext: (ctx: AppsContext) => void;
  setAiAll: (on: boolean) => void;
  setDjiField: <K extends keyof DjiConfig>(key: K, value: DjiConfig[K]) => void;
  saveDjiConfig: () => { configured: boolean; changed: boolean; wasConfigured: boolean };
  appendActivity: (appId: AppId, text: string, kind?: ActivityKind) => void;
}

export const AI_AGENT_CONTEXTS: AppsContext[] = ['drones', 'oi', 'surv', 'infra', 'rtm', 'inv'];

export const APPS_CONTEXT_LABEL: Record<AppsContext, string> = {
  drones: 'Drones',
  oi: 'Operational Intents',
  surv: 'Surveillance',
  infra: 'Infrastructure',
  rtm: 'RTM Services',
  inv: 'Inventory',
};

export const MISSION_TEMPLATES: MissionTemplate[] = [
  {
    id: 'gps_box',
    name: 'GPS Point / Box Inspection',
    description: 'Fly a square inspection pattern centered on the triggering GPS point.',
    hasBoxDistance: true,
  },
  {
    id: 'perimeter_sweep',
    name: 'Perimeter Sweep',
    description: 'Fly the tenant-defined perimeter boundary once.',
    hasBoxDistance: false,
  },
  {
    id: 'linear_corridor',
    name: 'Linear Corridor Scan',
    description: 'Fly a linear corridor mission along the pre-configured route.',
    hasBoxDistance: false,
  },
];

const MAX_ACTIVITY = 30;

function makeId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function seedActivity(): Record<AppId, AppActivity[]> {
  if (!config.isMock) return { aiagent: [], djifh2: [] };
  return {
    aiagent: [
      {
        id: makeId('seed'),
        appId: 'aiagent',
        at: new Date(Date.now() - 3 * 60_000).toISOString(),
        text: 'Standing by — no contexts enabled yet.',
        kind: 'seed',
      },
    ],
    djifh2: [
      {
        id: makeId('seed'),
        appId: 'djifh2',
        at: new Date(Date.now() - 5 * 60_000).toISOString(),
        text: 'Waiting for configuration — MQTT broker not connected.',
        kind: 'seed',
      },
    ],
  };
}

const INITIAL_DJI: DjiConfig = {
  orgId: '',
  apiKey: '',
  apiKeyPresent: false,
  brokerTopic: '',
  triggerType: 'workflow',
  workflowName: '',
  missionTemplateId: 'gps_box',
  boxDistanceM: 300,
  configured: false,
  savedAt: null,
};

function trimActivity(items: AppActivity[]) {
  return items.slice(0, MAX_ACTIVITY);
}

export const useAppsStore = create<AppsStore>()(
  devtools(
    persist(
      (set, get) => ({
        selectedApp: null,
        activeTab: 'config',
        aiEnabled: {
          drones: false,
          oi: false,
          surv: false,
          infra: false,
          rtm: false,
          inv: false,
        },
        dji: INITIAL_DJI,
        activity: seedActivity(),

        selectApp: (app) => set({ selectedApp: app, activeTab: 'config' }),
        setActiveTab: (tab) => set({ activeTab: tab }),
        toggleAiContext: (ctx) =>
          set((s) => ({
            aiEnabled: { ...s.aiEnabled, [ctx]: !s.aiEnabled[ctx] },
          })),
        setAiAll: (on) =>
          set({
            aiEnabled: Object.fromEntries(AI_AGENT_CONTEXTS.map((ctx) => [ctx, on])) as Record<
              AppsContext,
              boolean
            >,
          }),
        setDjiField: (key, value) => set((s) => ({ dji: { ...s.dji, [key]: value } })),
        saveDjiConfig: () => {
          const prev = get().dji;
          const next = { ...prev };
          next.orgId = next.orgId.trim();
          next.apiKey = next.apiKey.trim();
          next.brokerTopic = next.brokerTopic.trim();
          next.workflowName = next.workflowName.trim();
          next.boxDistanceM = Math.max(100, Math.min(1000, Math.round(next.boxDistanceM || 300)));
          next.apiKeyPresent = next.apiKey.length > 0;
          const hasCreds = !!(next.orgId && next.apiKeyPresent && next.brokerTopic);
          const hasTrigger =
            next.triggerType === 'workflow' ? !!next.workflowName : !!next.missionTemplateId;
          next.configured = hasCreds && hasTrigger;
          next.savedAt = new Date().toISOString();
          const changed = JSON.stringify(prev) !== JSON.stringify(next);
          set({ dji: next });
          return { configured: next.configured, changed, wasConfigured: prev.configured };
        },
        appendActivity: (appId, text, kind = 'config') =>
          set((s) => ({
            activity: {
              ...s.activity,
              [appId]: trimActivity([
                {
                  id: makeId(appId),
                  appId,
                  at: new Date().toISOString(),
                  text,
                  kind,
                },
                ...s.activity[appId],
              ]),
            },
          })),
      }),
      {
        name: 'flysafe-v2-apps',
        partialize: (s) => ({
          selectedApp: s.selectedApp,
          activeTab: s.activeTab,
          aiEnabled: s.aiEnabled,
          dji: config.isMock
            ? s.dji
            : {
                ...s.dji,
                apiKey: '',
                apiKeyPresent: false,
                configured: false,
              },
          activity: s.activity,
        }),
      },
    ),
    { name: 'apps' },
  ),
);
